import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { ShowRow } from "@/components/ShowRow";
import { splitShows } from "@/data/site";

const FILTERS = ["All", "Austin", "San Antonio"];

export default function Shows() {
  const [filter, setFilter] = useState("All");
  const byCity = (s) => filter === "All" || s.city.includes(filter);
  const { upcoming, past } = splitShows();
  const upcomingShows = upcoming.filter(byCity);
  const pastShows = past.filter(byCity);

  return (
    <main className="pt-32">
      <Seo
        title="Shows — 1001 Nights Orchestra"
        siteName="1001 Nights Orchestra"
        description="Upcoming dates and recent performances by 1001 Nights Orchestra — concerts, folk dance nights and festival appearances in Austin, San Antonio and beyond."
      />
      <header className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-14">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-amber-400">Season 2026</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-amber-50 tracking-tight">
            Upcoming <span className="italic gold-text">Shows</span>
          </h1>
          <p className="mt-4 text-slate-400 max-w-xl leading-relaxed">
            From concert halls to late-night lounges — every performance is a different
            constellation of the ensemble.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-3">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`show-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 border transition-all duration-300 ${
                filter === f
                  ? "border-gold bg-gold/10 text-amber-200"
                  : "border-amber-500/15 text-slate-400 hover:border-amber-400/40 hover:text-amber-200"
              }`}
            >
              {f}
            </button>
          ))}
        </Reveal>
      </header>

      <section data-testid="shows-list" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16">
        {upcomingShows.length === 0 ? (
          <Reveal>
            <p
              data-testid="shows-empty"
              className="font-serif text-2xl text-slate-400 py-10 border-t border-b border-amber-500/10 flex flex-wrap items-baseline gap-x-3 gap-y-2"
            >
              No upcoming dates announced yet.
              <Link
                to="/booking"
                data-testid="shows-empty-booking-link"
                className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-amber-300 hover:text-amber-100 transition-colors"
              >
                Book the orchestra <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </p>
          </Reveal>
        ) : (
          <div className="border-t border-amber-500/10">
            {upcomingShows.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05}>
                <ShowRow show={s} />
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {pastShows.length > 0 && (
        <section data-testid="past-shows-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24">
          <Reveal className="mb-10">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-amber-400">Archive</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-amber-50 tracking-tight">
              Recent <span className="italic gold-text">Performances</span>
            </h2>
          </Reveal>
          <div data-testid="past-shows-list" className="border-t border-amber-500/10">
            {pastShows.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05}>
                <ShowRow show={s} past />
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
